import type {
  MissionAssessmentServiceSelectDto,
  MissionAssessmentTaskRecommendationSelectDto,
  SurveySelectDto,
  SurveyUseDomainNestedSelectDto,
  TechnologySelectDto,
  UserSelectDto,
} from '@@types/api.types';
import { AssessmentSubtype, AssessmentType } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { ExpertProfileButton } from '@components/shared/expert-profile-button.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { useDelayedContentVisibility } from '@hooks/use-delayed-content-visibility.hook';
import { cn } from '@lib/utils';
import { useGetCategories } from '@queries/category.query';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentServicesQueryOptions,
} from '@queries/mission-assessments.query';
import { useSurveySidebarStore } from '@stores/survey-sidebar.store';
import { useQuery } from '@tanstack/react-query';
import { formatDateUtc } from '@utils/date.util';
import { formatProper } from '@utils/text.util';
import { ExternalLink } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { handleCollapsedSidebarItemClick } from './survey-sidebar-click.utils';

// ─────────────────────────────────────────────────────────────────────────────
// Accordion Item Wrapper
// ─────────────────────────────────────────────────────────────────────────────

const ACCORDION_ITEM_CLASSES = {
  default:
    'rounded-xl border-0 bg-[#ffffff29] transition-colors delay-150 duration-200 data-[state=open]:bg-white data-[state=open]:delay-0',
  military:
    'rounded-xl border-0 bg-[#d0d7d1]/20 transition-colors delay-150 duration-200 data-[state=open]:bg-white data-[state=open]:outline data-[state=open]:outline-4 data-[state=open]:outline-[#8e9e8e] data-[state=open]:delay-0 data-[state=closed]:bg-[#657865]',
} as const;

type SidebarAccordionItemProps = {
  children: ReactNode;
  title: string;
  value: string;
  variant?: keyof typeof ACCORDION_ITEM_CLASSES;
};

const SidebarAccordionItem: FC<SidebarAccordionItemProps> = (props) => {
  const { children, title, value, variant = 'default' } = props;

  return (
    <AccordionItem
      className={ACCORDION_ITEM_CLASSES[variant]}
      value={value}
    >
      <AccordionTrigger className="px-4 py-3 text-sm font-medium text-white hover:no-underline data-[state=open]:text-gray-700">
        {title}
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 data-[state=closed]:animate-none! data-[state=closed]:h-0!">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Helper to extract unique uses and domains from surveyUseDomains
// (surveyUseDomains contains all use-domain pairs/combinations, but we want unique items)
function extractUniqueUsesAndDomains(surveyUseDomains: SurveyUseDomainNestedSelectDto[]): {
  domains: { id: string; name: string }[];
  uses: { id: string; name: string }[];
} {
  const usesMap = new Map<string, { id: string; name: string }>();
  const domainsMap = new Map<string, { id: string; name: string }>();

  for (const sud of surveyUseDomains) {
    if (!usesMap.has(sud.useId)) {
      usesMap.set(sud.useId, { id: sud.useId, name: sud.use.name });
    }
    if (!domainsMap.has(sud.domainId)) {
      domainsMap.set(sud.domainId, { id: sud.domainId, name: sud.domain.name });
    }
  }

  return {
    domains: Array.from(domainsMap.values()),
    uses: Array.from(usesMap.values()),
  };
}

type SurveyParametersSidebarProps = {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  survey?: SurveySelectDto;
  technology?: TechnologySelectDto;
};

type CollapsedContextItem = {
  id: string;
  label: string;
};

export const SurveyParametersSidebar: FC<SurveyParametersSidebarProps> = (props) => {
  const { isCollapsed, onToggleCollapsed, survey, technology } = props;
  const { technologyId } = useParams<{ technologyId: string }>();

  const isTrl = survey?.assessmentType === AssessmentType.TRL;
  const isMilitaryTrl =
    survey?.assessmentType === AssessmentType.TRL && survey?.assessmentSubtype === AssessmentSubtype.MILITARY;
  const missionAssessmentId = survey?.missionAssessmentId;

  const { contextOpenAccordions, setContextOpenAccordions } = useSurveySidebarStore();
  const isContentVisible = useDelayedContentVisibility({ delay: 200, isCollapsed });

  // Categories for subcategory lookup
  const { data: categories = [] } = useGetCategories();

  // Military TRL specific queries
  const { data: servicesData, isLoading: isLoadingServices } = useQuery({
    ...useMissionAssessmentServicesQueryOptions(missionAssessmentId ?? ''),
    enabled: isMilitaryTrl && !!missionAssessmentId,
  });
  const { data: coreData, isLoading: isLoadingCore } = useQuery({
    ...useMissionAssessmentCoreQueryOptions(missionAssessmentId ?? ''),
    enabled: isMilitaryTrl && !!missionAssessmentId,
  });

  // Get subcategory from categories
  const subcategories = categories.flatMap((category) => category.subcategories);
  const subcategory = technology ? subcategories.find((sub) => sub.id === technology.subcategoryId) : undefined;

  const taskRecommendations = servicesData?.taskRecommendations ?? [];
  const recommendedServices = servicesData?.recommendedServices ?? [];
  const expert = coreData?.expert;
  const createdAt = coreData?.createdAt;

  const hasTaskRecommendations = taskRecommendations.length > 0;
  const hasRecommendedServices = recommendedServices.length > 0;
  const hasExpert = !!expert;
  const surveyUseDomains = survey?.surveyUseDomains ?? [];
  const { domains, uses } = extractUniqueUsesAndDomains(surveyUseDomains);
  const hasUsesOrDomains = uses.length > 0 || domains.length > 0;
  const collapsedContextItems: CollapsedContextItem[] = [
    { id: 'technology-overview', label: 'T' },
    ...(isTrl && hasUsesOrDomains ? [{ id: 'use-domain', label: 'U' }] : []),
    ...(isMilitaryTrl && hasRecommendedServices ? [{ id: 'recommended-services', label: 'S' }] : []),
    ...(isMilitaryTrl && hasTaskRecommendations ? [{ id: 'task-recommendations', label: 'R' }] : []),
    ...(isMilitaryTrl && hasExpert ? [{ id: 'mission-assessment', label: 'M' }] : []),
  ];

  const isLoading = !survey || !technology || (isMilitaryTrl && (isLoadingServices || isLoadingCore));

  const maUrl =
    technologyId && missionAssessmentId ? `/technology/${technologyId}?tab=ma&maId=${missionAssessmentId}` : undefined;

  const handleOpenMissionAssessment = (): void => {
    if (maUrl) {
      window.open(maUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleExpandCollapsedSidebar = (): void => {
    onToggleCollapsed();
  };

  return (
    <>
      <div
        className={cn(
          'flex h-full shrink-0 flex-col overflow-hidden transition-[width] duration-300 ease-in-out',
          isCollapsed
            ? 'w-[52px] cursor-w-resize'
            : 'w-[136px] @min-[36rem]/survey-shell:w-[160px] @min-[42rem]/survey-shell:w-[200px] @min-[48rem]/survey-shell:w-[240px] @min-[64rem]/survey-shell:w-[272px] @min-[80rem]/survey-shell:w-[320px] @min-[96rem]/survey-shell:w-[360px]',
        )}
        style={{
          backgroundColor: '#09005C',
          backgroundImage:
            'radial-gradient(65.88% 80.53% at 65.88% 19.47%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%)',
        }}
      >
        {isCollapsed ? (
          <div
            className="flex flex-1 cursor-w-resize flex-col items-center gap-2 px-1.5 py-4"
            onClick={onToggleCollapsed}
          >
            {collapsedContextItems.length > 0 ? (
              <>
                {collapsedContextItems.slice(0, 6).map((item) => {
                  const contextItemIsOpen = contextOpenAccordions.includes(item.id);

                  return (
                    <div
                      className={cn(
                        'flex w-full items-center justify-center rounded-md border px-1 py-2 text-[10px] font-semibold leading-none transition-colors',
                        contextItemIsOpen
                          ? 'border-[#8D83FF] bg-white/30 text-white'
                          : 'border-white/20 bg-white/10 text-white/70',
                      )}
                      key={item.id}
                      onClick={(event) =>
                        handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                      }
                    >
                      {item.label}
                    </div>
                  );
                })}
                {collapsedContextItems.length > 6 && (
                  <div
                    className="pt-1 text-[10px] font-medium text-white/70"
                    onClick={(event) =>
                      handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                    }
                  >
                    +{collapsedContextItems.length - 6}
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  className="h-8 w-full rounded-md border border-white/20 bg-white/10"
                  onClick={(event) =>
                    handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                  }
                />
                <div
                  className="h-8 w-full rounded-md border border-white/20 bg-white/10"
                  onClick={(event) =>
                    handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                  }
                />
                <div
                  className="h-8 w-full rounded-md border border-white/20 bg-white/10"
                  onClick={(event) =>
                    handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                  }
                />
              </>
            )}
          </div>
        ) : isLoading ? (
          // Loading state - empty container to prevent layout shift
          <div className="flex-1" />
        ) : (
          <div
            className={cn(
              'min-h-0 flex-1 basis-0 overflow-y-auto px-4 pb-4 pt-7 transition-opacity duration-150',
              isContentVisible ? 'opacity-100' : 'opacity-0',
            )}
          >
            <Accordion
              className="flex flex-col gap-3"
              onValueChange={setContextOpenAccordions}
              type="multiple"
              value={contextOpenAccordions}
            >
              {/* Technology Overview - Always visible */}
              <SidebarAccordionItem
                title="Technology"
                value="technology-overview"
              >
                <TechnologyOverviewContent
                  subcategory={subcategory}
                  technology={technology}
                />
              </SidebarAccordionItem>

              {/* Use & Domain - TRL only */}
              {isTrl && hasUsesOrDomains && (
                <SidebarAccordionItem
                  title="Use & Domain"
                  value="use-domain"
                >
                  <UseDomainContent
                    domains={domains}
                    uses={uses}
                  />
                </SidebarAccordionItem>
              )}

              {/* Recommended Services - Military TRL only */}
              {isMilitaryTrl && hasRecommendedServices && (
                <SidebarAccordionItem
                  title={`Recommended Services (${recommendedServices.length})`}
                  value="recommended-services"
                  variant="military"
                >
                  <RecommendedServicesContent recommendedServices={recommendedServices} />
                </SidebarAccordionItem>
              )}

              {/* Task Recommendations - Military TRL only */}
              {isMilitaryTrl && hasTaskRecommendations && (
                <SidebarAccordionItem
                  title={`Task Recommendations (${taskRecommendations.length})`}
                  value="task-recommendations"
                  variant="military"
                >
                  <TaskRecommendationsContent taskRecommendations={taskRecommendations} />
                </SidebarAccordionItem>
              )}

              {/* Mission Assessment - Military TRL only */}
              {isMilitaryTrl && hasExpert && (
                <SidebarAccordionItem
                  title="Mission Assessment"
                  value="mission-assessment"
                  variant="military"
                >
                  <MissionAssessmentContent
                    createdAt={createdAt}
                    expert={expert}
                    maUrl={maUrl}
                    onOpenMissionAssessment={handleOpenMissionAssessment}
                  />
                </SidebarAccordionItem>
              )}
            </Accordion>
          </div>
        )}
      </div>
    </>
  );
};

// Technology Overview Content Component
type TechnologyOverviewContentProps = {
  subcategory?: { description?: string; id: string; name: string };
  technology: TechnologySelectDto;
};

const TechnologyOverviewContent: FC<TechnologyOverviewContentProps> = (props) => {
  const { subcategory, technology } = props;

  return (
    <div className="flex flex-col gap-2 text-sm font-medium text-gray-600">
      <div className="flex min-w-0 flex-col">
        <div className="truncate text-sm font-medium text-gray-800">{technology.name}</div>
        <BlueLabel text={`${formatProper(technology.type)} Technology`} />
      </div>

      {technology.category && (
        <div className="flex flex-col gap-0.5">
          {technology.category.description ? (
            <TooltipComponent
              className="whitespace-pre-wrap text-xs font-normal"
              side="right"
              trigger={
                <div>
                  <span className="cursor-help border-b inline-block w-auto border-dashed border-gray-300 font-medium">
                    {technology.category.name}
                  </span>
                </div>
              }
            >
              {technology.category.description}
            </TooltipComponent>
          ) : (
            <span className="font-medium">{technology.category.name}</span>
          )}
          <BlueLabel text="Category" />
        </div>
      )}

      {subcategory && (
        <div className="flex flex-col gap-0.5">
          {subcategory.description ? (
            <TooltipComponent
              className="whitespace-pre-wrap text-xs font-normal"
              side="right"
              trigger={
                <span className="cursor-help border-b border-dashed border-gray-300 font-medium">
                  {subcategory.name}
                </span>
              }
            >
              {subcategory.description}
            </TooltipComponent>
          ) : (
            <span className="font-medium">{subcategory.name}</span>
          )}
          <BlueLabel text="Subcategory" />
        </div>
      )}
    </div>
  );
};

// Use & Domain Content Component
type UseDomainContentProps = {
  domains: { id: string; name: string }[];
  uses: { id: string; name: string }[];
};

const UseDomainContent: FC<UseDomainContentProps> = (props) => {
  const { domains, uses } = props;

  return (
    <div className="flex flex-col gap-2 text-sm font-medium text-gray-600">
      {uses.length > 0 && (
        <div>
          <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600">
            {uses.map((use) => use.name).join(', ')}
          </div>
          <BlueLabel text={uses.length === 1 ? 'Use' : 'Uses'} />
        </div>
      )}

      {domains.length > 0 && (
        <div>
          <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600">
            {domains.map((domain) => domain.name).join(', ')}
          </div>
          <BlueLabel text={domains.length === 1 ? 'Domain' : 'Domains'} />
        </div>
      )}
    </div>
  );
};

// Recommended Services Content Component
type RecommendedServicesContentProps = {
  recommendedServices: MissionAssessmentServiceSelectDto[];
};

const RecommendedServicesContent: FC<RecommendedServicesContentProps> = (props) => {
  const { recommendedServices } = props;

  return (
    <div className="flex flex-col gap-y-3">
      {recommendedServices.map(({ service, unit }, index) => (
        <AvatarItem
          altText={`${service.name} Icon`}
          className="min-w-0 items-center"
          imageUrl={`/images/services/${service.slug}.png`}
          initialText={service.name}
          key={`${service.id}-${index}`}
          size="lg"
          subtitle={unit}
          title={service.name}
        />
      ))}
    </div>
  );
};

// Task Recommendations Content Component
type TaskRecommendationsContentProps = {
  taskRecommendations: MissionAssessmentTaskRecommendationSelectDto[];
};

const TaskRecommendationsContent: FC<TaskRecommendationsContentProps> = (props) => {
  const { taskRecommendations } = props;

  return (
    <div className="space-y-2">
      {taskRecommendations.map((taskRecommendation) => (
        <div
          className="wrap-break-word rounded-md border border-gray-200 bg-gray-50 p-2 text-sm text-gray-600"
          key={taskRecommendation.id}
        >
          <ExpandableText
            maxLines={2}
            text={taskRecommendation.description}
          />
        </div>
      ))}
    </div>
  );
};

// Mission Assessment Content Component
type MissionAssessmentContentProps = {
  createdAt?: string;
  expert?: UserSelectDto;
  maUrl?: string;
  onOpenMissionAssessment: () => void;
};

const MissionAssessmentContent: FC<MissionAssessmentContentProps> = (props) => {
  const { createdAt, expert, maUrl, onOpenMissionAssessment } = props;

  return (
    <div className="flex flex-col gap-4">
      {expert && (
        <div className="flex flex-col gap-1">
          <BlueLabel
            className="text-xs"
            text="Assessed by"
          />
          <ExpertProfileButton expert={expert} />
        </div>
      )}
      {createdAt && (
        <TextWithLabel
          className="flex-col-reverse text-sm"
          label="Assessment Date"
          labelClassName="text-xs"
          title={formatDateUtc(createdAt)}
        />
      )}
      {maUrl && (
        <Button
          className="mt-2 w-full gap-2"
          onClick={onOpenMissionAssessment}
          type="button"
          variant="secondary"
        >
          <span>Open Mission Assessment</span>
          <ExternalLink className="size-4" />
        </Button>
      )}
    </div>
  );
};
